import type { Layer, PixelData, Psd } from 'ag-psd'

export type CompatibilityLevel = 'exact' | 'review' | 'unsupported'

export interface LayerNode {
  id: string
  layer: Layer
  name: string
  depth: number
  path: string[]
  isGroup: boolean
  descendants: string[]
  compatibility: CompatibilityLevel
  compatibilityNote: string
}

export interface PsdDocument {
  psd: Psd
  nodes: LayerNode[]
  nodeMap: Map<string, LayerNode>
  iccProfile?: Uint8Array
  iccProfileName?: string
  sourceBytes: number
}

const COLOR_MODES: Record<number, string> = {
  0: 'Bitmap',
  1: 'Grayscale',
  2: 'Indexed',
  3: 'RGB',
  4: 'CMYK',
  7: 'Multichannel',
  8: 'Duotone',
  9: 'Lab',
}

export function colorModeName(mode?: number) {
  return mode === undefined ? 'Unknown' : COLOR_MODES[mode] ?? `Mode ${mode}`
}

function readU16(view: DataView, offset: number) {
  return view.getUint16(offset, false)
}

function readU32(view: DataView, offset: number) {
  return view.getUint32(offset, false)
}

function ascii(bytes: Uint8Array) {
  return String.fromCharCode(...bytes)
}

function readIccDescription(profile: Uint8Array) {
  try {
    if (profile.length < 132) return undefined
    const view = new DataView(profile.buffer, profile.byteOffset, profile.byteLength)
    const tagCount = readU32(view, 128)
    for (let index = 0; index < Math.min(tagCount, 128); index += 1) {
      const entry = 132 + index * 12
      if (entry + 12 > profile.length) break
      if (ascii(profile.subarray(entry, entry + 4)) !== 'desc') continue
      const offset = readU32(view, entry + 4)
      const size = readU32(view, entry + 8)
      if (offset + Math.min(size, 12) > profile.length) return undefined
      const type = ascii(profile.subarray(offset, offset + 4))
      if (type === 'desc' && offset + 12 <= profile.length) {
        const length = Math.min(readU32(view, offset + 8), size - 12)
        return new TextDecoder('latin1').decode(profile.subarray(offset + 12, offset + 12 + Math.max(0, length - 1)))
      }
      if (type === 'mluc' && offset + 28 <= profile.length) {
        const recordOffset = readU32(view, offset + 24)
        const recordLength = readU32(view, offset + 20)
        const start = offset + recordOffset
        return new TextDecoder('utf-16be').decode(profile.subarray(start, start + recordLength))
      }
    }
  } catch {
    return undefined
  }
  return undefined
}

export function extractIccProfile(buffer: ArrayBuffer) {
  try {
    const bytes = new Uint8Array(buffer)
    const view = new DataView(buffer)
    if (ascii(bytes.subarray(0, 4)) !== '8BPS' || bytes.length < 30) return {}
    let offset = 26
    const colorDataLength = readU32(view, offset)
    offset += 4 + colorDataLength
    const resourcesLength = readU32(view, offset)
    offset += 4
    const resourcesEnd = Math.min(bytes.length, offset + resourcesLength)

    while (offset + 12 <= resourcesEnd) {
      const signature = ascii(bytes.subarray(offset, offset + 4))
      offset += 4
      const id = readU16(view, offset)
      offset += 2
      const nameLength = bytes[offset]
      offset += 1 + nameLength
      if ((1 + nameLength) % 2 !== 0) offset += 1
      const size = readU32(view, offset)
      offset += 4
      if (offset + size > resourcesEnd) break
      if ((signature === '8BIM' || signature === 'MeSa') && id === 1039) {
        const profile = bytes.slice(offset, offset + size)
        return { profile, name: readIccDescription(profile) }
      }
      offset += size + (size % 2)
    }
  } catch {
    return {}
  }
  return {}
}

function compatibilityFor(layer: Layer): Pick<LayerNode, 'compatibility' | 'compatibilityNote'> {
  if (layer.children) return { compatibility: 'exact', compatibilityNote: 'グループ内のレイヤーを個別に選択できます' }
  if (!layer.imageData) return { compatibility: 'unsupported', compatibilityNote: '書き出せるピクセルキャッシュがありません' }
  if (layer.imageData.data instanceof Float32Array) {
    return { compatibility: 'unsupported', compatibilityNote: '32 bit/channel PNG は未対応です' }
  }
  const details = layer as Layer & Record<string, unknown>
  const maskNeedsPhotoshop = layer.mask && (
    layer.mask.userMaskDensity !== undefined ||
    Boolean(layer.mask.userMaskFeather) ||
    layer.mask.fromVectorData
  )
  if (layer.effects || details.vectorMask || layer.clipping || details.adjustment || maskNeedsPhotoshop) {
    return { compatibility: 'review', compatibilityNote: '効果・ベクトルマスク・クリッピングはPhotoshopと差が出る可能性があります' }
  }
  return { compatibility: 'exact', compatibilityNote: '埋め込みピクセルを直接書き出せます' }
}

export function buildDocument(psd: Psd, source: ArrayBuffer): PsdDocument {
  const nodes: LayerNode[] = []
  let sequence = 0

  const walk = (layers: Layer[], depth: number, parentPath: string[]): string[] => {
    const directIds: string[] = []
    layers.forEach((layer) => {
      const id = `layer-${sequence++}`
      const name = layer.name?.trim() || `Layer ${sequence}`
      const path = [...parentPath, name]
      const node: LayerNode = {
        id,
        layer,
        name,
        depth,
        path,
        isGroup: Boolean(layer.children),
        descendants: [],
        ...compatibilityFor(layer),
      }
      nodes.push(node)
      directIds.push(id)
      if (layer.children) node.descendants = walk(layer.children, depth + 1, path)
    })
    return directIds.flatMap((id) => {
      const node = nodes.find((candidate) => candidate.id === id)!
      return node.isGroup ? node.descendants : [id]
    })
  }

  walk(psd.children ?? [], 0, [])
  const { profile, name } = extractIccProfile(source)
  return {
    psd,
    nodes,
    nodeMap: new Map(nodes.map((node) => [node.id, node])),
    iccProfile: profile,
    iccProfileName: name,
    sourceBytes: source.byteLength,
  }
}

function alphaMax(data: PixelData['data']) {
  return data instanceof Uint16Array ? 65535 : data instanceof Float32Array ? 1 : 255
}

function clonePixels(image: PixelData): PixelData {
  return {
    width: image.width,
    height: image.height,
    data: image.data.slice() as PixelData['data'],
  }
}

function maskValue(mask: PixelData, index: number) {
  const value = mask.data[index * 4]
  if (mask.data instanceof Uint16Array) return value / 65535
  if (mask.data instanceof Float32Array) return value
  return value / 255
}

export function renderLayerPixels(layer: Layer, applyMask: boolean, applyOpacity: boolean) {
  if (!layer.imageData) return undefined
  const result = clonePixels(layer.imageData)
  const max = alphaMax(result.data)
  const opacity = applyOpacity ? (layer.opacity ?? 1) : 1
  const mask = applyMask && !layer.mask?.disabled ? layer.mask : undefined
  const maskImage = mask?.imageData
  const layerLeft = layer.left ?? 0
  const layerTop = layer.top ?? 0
  const maskLeft = mask?.positionRelativeToLayer ? layerLeft + (mask.left ?? 0) : (mask?.left ?? layerLeft)
  const maskTop = mask?.positionRelativeToLayer ? layerTop + (mask.top ?? 0) : (mask?.top ?? layerTop)
  const fallbackMask = (mask?.defaultColor ?? 255) / 255

  for (let y = 0; y < result.height; y += 1) {
    for (let x = 0; x < result.width; x += 1) {
      const pixel = y * result.width + x
      let maskAlpha = 1
      if (maskImage) {
        const maskX = layerLeft + x - maskLeft
        const maskY = layerTop + y - maskTop
        maskAlpha = maskX >= 0 && maskX < maskImage.width && maskY >= 0 && maskY < maskImage.height
          ? maskValue(maskImage, maskY * maskImage.width + maskX)
          : fallbackMask
      }
      const alphaIndex = pixel * 4 + 3
      const nextAlpha = Number(result.data[alphaIndex]) * opacity * maskAlpha
      result.data[alphaIndex] = result.data instanceof Float32Array ? nextAlpha : Math.round(nextAlpha)
      if (result.data[alphaIndex] > max) result.data[alphaIndex] = max
    }
  }
  return result
}

export function toDisplayImageData(source: PixelData) {
  if (source.data instanceof Uint8ClampedArray) return new ImageData(source.data.slice(), source.width, source.height)
  const output = new Uint8ClampedArray(source.width * source.height * 4)
  if (source.data instanceof Uint16Array) {
    for (let index = 0; index < output.length; index += 1) output[index] = Math.round(source.data[index] / 257)
  } else if (source.data instanceof Float32Array) {
    for (let index = 0; index < output.length; index += 1) output[index] = Math.round(Math.max(0, Math.min(1, source.data[index])) * 255)
  } else {
    output.set(source.data)
  }
  return new ImageData(output, source.width, source.height)
}

export function documentCompatibility(document: PsdDocument) {
  const mode = document.psd.colorMode
  const bits = document.psd.bitsPerChannel ?? 8
  if (mode !== 3 || bits === 32) return 'review' as const
  if (document.nodes.some((node) => node.compatibility === 'unsupported')) return 'review' as const
  return 'exact' as const
}
